import { useEffect, useState, useCallback } from 'react';
import { ArrowLeft, RotateCw } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Avatar from '../ui/Avatar';
import { Skeleton } from '../Skeleton';
import {
    getSubmission,
    getAssessment,
    getAssessmentReview,
    getFeedbackTemplate,
    regenerateAssessmentReview,
    regenerateFeedbackTemplate,
    retryAssessment,
    MODULE_LABELS,
    type Submission,
    type Assessment,
    type AssessmentReview,
    type FeedbackTemplate,
    type Report,
} from '../../mentorshipService';
import ModuleAssessmentCard from './ModuleAssessmentCard';
import AssessmentReviewSummary from './AssessmentReviewSummary';
import FeedbackTemplateOptions from './FeedbackTemplateOptions';
import ScoreAdjustmentRow from './ScoreAdjustmentRow';
import ReportSubmitModal from './ReportSubmitModal';

type Props = {
    submission: Submission;
    onBack: () => void;
    onFinalized: (report: Report) => void;
};

function menteeName(s: Submission): string {
    const m = s.mentee_summary;
    if (!m) return `Mentee #${s.mentee}`;
    return `${m.first_name} ${m.last_name}`.trim() || m.email || `Mentee #${m.id}`;
}

function AssessmentReviewer({ submission: initialSubmission, onBack, onFinalized }: Props) {
    const [submission, setSubmission] = useState<Submission>(initialSubmission);
    const [assessment, setAssessment] = useState<Assessment | null>(null);
    const [review, setReview] = useState<AssessmentReview | null>(null);
    const [template, setTemplate] = useState<FeedbackTemplate | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [notes, setNotes] = useState('');
    const [finalScore, setFinalScore] = useState<number | null>(null);
    const [overrideEnabled, setOverrideEnabled] = useState(false);
    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const [regeneratingReview, setRegeneratingReview] = useState(false);
    const [regeneratingTemplate, setRegeneratingTemplate] = useState(false);

    const loadAll = useCallback(
        async (isCancelled?: () => boolean) => {
            setLoading(true);
            setError(null);
            try {
                const fresh = await getSubmission(initialSubmission.id);
                if (isCancelled?.()) return;
                setSubmission(fresh);

                let a: Assessment | null = null;
                if (fresh.assessment_id) {
                    try {
                        a = await getAssessment(fresh.assessment_id);
                    } catch {
                        a = null;
                    }
                }
                if (isCancelled?.()) return;
                setAssessment(a);

                if (a && a.state === 'ready') {
                    try {
                        const [rev, tpl] = await Promise.all([
                            getAssessmentReview(a.id),
                            getFeedbackTemplate(a.id),
                        ]);
                        if (isCancelled?.()) return;
                        setReview(rev);
                        setTemplate(tpl);
                        setFinalScore(a.score_ai);
                    } catch (err) {
                        console.error('[AssessmentReviewer] assist load failed', err);
                    }
                }
            } catch (err) {
                console.error('[AssessmentReviewer] load failed', err);
                if (!isCancelled?.()) setError('Failed to load assessment.');
            } finally {
                if (!isCancelled?.()) setLoading(false);
            }
        },
        [initialSubmission.id],
    );

    useEffect(() => {
        let cancelled = false;
        loadAll(() => cancelled);
        return () => {
            cancelled = true;
        };
    }, [loadAll]);

    const handleRetryAssessment = async () => {
        setRetrying(true);
        try {
            const a = await retryAssessment(submission.id);
            setAssessment(a);
            await loadAll();
        } catch (err) {
            console.error('[AssessmentReviewer] retry failed', err);
        } finally {
            setRetrying(false);
        }
    };

    const handleRegenerateReview = async () => {
        if (!assessment) return;
        setRegeneratingReview(true);
        try {
            const r = await regenerateAssessmentReview(assessment.id);
            setReview(r);
        } finally {
            setRegeneratingReview(false);
        }
    };

    const handleRegenerateTemplate = async () => {
        if (!assessment) return;
        setRegeneratingTemplate(true);
        try {
            const t = await regenerateFeedbackTemplate(assessment.id);
            setTemplate(t);
        } finally {
            setRegeneratingTemplate(false);
        }
    };

    const insertTemplate = (text: string) => {
        setNotes((prev) => (prev ? `${prev}\n\n${text}` : text));
    };

    if (loading) {
        return (
            <div className="assessment-reviewer">
                <button
                    type="button"
                    className="mentorship-stack__back"
                    onClick={onBack}
                    aria-label="Back to queue"
                >
                    <ArrowLeft size={18} /> Back
                </button>
                <Skeleton width="100%" height="120px" borderRadius="12px" />
                <Skeleton width="100%" height="200px" borderRadius="12px" />
            </div>
        );
    }

    if (error || !assessment) {
        return (
            <div className="assessment-reviewer">
                <button
                    type="button"
                    className="mentorship-stack__back"
                    onClick={onBack}
                    aria-label="Back to queue"
                >
                    <ArrowLeft size={18} /> Back
                </button>
                <div className="empty-state">
                    <h3>Assessment unavailable</h3>
                    <p>{error ?? 'No AI assessment exists for this submission yet.'}</p>
                    <Button
                        variant="primary"
                        size="md"
                        icon={<RotateCw size={16} />}
                        onClick={handleRetryAssessment}
                        disabled={retrying}
                    >
                        {retrying ? 'Retrying…' : 'Retry assessment'}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="assessment-reviewer">
            <button
                type="button"
                className="mentorship-stack__back"
                onClick={onBack}
                aria-label="Back to queue"
            >
                <ArrowLeft size={18} /> Back
            </button>

            <Card variant="row" className="assessment-reviewer__header-card">
                <Card.Header>
                    <Avatar name={menteeName(submission)} size="md" />
                    <div>
                        <p className="assessment-reviewer__name">{menteeName(submission)}</p>
                        <p className="assessment-reviewer__meta">
                            {MODULE_LABELS[submission.module]} · {submission.quarter}
                        </p>
                    </div>
                </Card.Header>
            </Card>

            <Card variant="stacked" className="assessment-reviewer__content-card">
                <Card.Header>
                    <h3>Mentee submission</h3>
                </Card.Header>
                <Card.Body>
                    <p className="assessment-reviewer__content">{submission.content}</p>
                </Card.Body>
            </Card>

            <ModuleAssessmentCard assessment={assessment} />

            {assessment.state === 'schema_error' && (
                <Button
                    variant="secondary"
                    size="md"
                    icon={<RotateCw size={16} />}
                    onClick={handleRetryAssessment}
                    disabled={retrying}
                >
                    {retrying ? 'Retrying…' : 'Retry assessment'}
                </Button>
            )}

            {assessment.state === 'ready' && review && (
                <AssessmentReviewSummary
                    review={review}
                    onRegenerate={handleRegenerateReview}
                    regenerating={regeneratingReview}
                />
            )}

            {assessment.state === 'ready' && template && (
                <FeedbackTemplateOptions
                    template={template}
                    onInsert={insertTemplate}
                    onRegenerate={handleRegenerateTemplate}
                    regenerating={regeneratingTemplate}
                />
            )}

            {assessment.state === 'ready' && assessment.score_ai !== null && (
                <>
                    <ScoreAdjustmentRow
                        aiScore={assessment.score_ai}
                        value={finalScore}
                        onChange={setFinalScore}
                        overrideEnabled={overrideEnabled}
                        onToggleOverride={() => setOverrideEnabled((v) => !v)}
                    />
                    <div className="assessment-reviewer__notes">
                        <label htmlFor="reviewer-notes">Mentor notes (preview)</label>
                        <textarea
                            id="reviewer-notes"
                            rows={5}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Click a quick template above or write your own…"
                        />
                    </div>
                    <Button
                        variant="primary"
                        size="lg"
                        onClick={() => setShowSubmitModal(true)}
                        disabled={finalScore === null}
                    >
                        Finalize report
                    </Button>
                </>
            )}

            {showSubmitModal && assessment.score_ai !== null && finalScore !== null && (
                <ReportSubmitModal
                    assessment={assessment}
                    review={review}
                    initialNotes={notes}
                    initialFinalScore={finalScore}
                    initialOverrideEnabled={overrideEnabled}
                    onClose={() => setShowSubmitModal(false)}
                    onSubmitted={(report) => {
                        setShowSubmitModal(false);
                        onFinalized(report);
                    }}
                />
            )}
        </div>
    );
}

export default AssessmentReviewer;
